import { Bugfender } from '@bugfender/sdk';
import { SubmissionResponse } from 'idmission-web-sdk';
import { LivenessCheckRequest } from 'idmission-web-sdk/dist/components/submission/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { getCurrentUser } from '@/api/auth';
import { getLocalDocumentUrlFromS3 } from '@/api/document';
import { getHolderDetailAndRequiredDocumentsRequest } from '@/api/operation';
import {
  initIdmissionSession,
  submitIdmissionResult,
} from '@/api/verification';

import { IS_PRODUCTION } from '@/constants/configs';
import { I18N_IDMISSION_FAILED_CODE_MESSAGE_MAP } from '@/constants/idmission';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';
import { useLivenessCheckRecorder } from '@/hooks/verification/useLivenessCheckRecorder';

import { notifyUserChanged } from '@/redux/auth/actions';
import { selectLoggedInUser } from '@/redux/auth/selectors';
import {
  selectConfigsState,
  selectLivenessrecordDisabled,
} from '@/redux/configs/selectors';
import { setOperationData } from '@/redux/operation';
import {
  selectOperationData,
  selectLivenessStatuses as selectOperationLivenessStatuses,
  selectOperationUser,
} from '@/redux/operation/selectors';
import { selectLivenessStatuses } from '@/redux/user-data/selectors';

import {
  IdentityVerificationResponse,
  VerificationResultDetails,
} from '@/types/api/idmission';
import {
  Document,
  DocumentSection,
  DocumentStatus,
  DocumentType,
} from '@/types/document';
import { OperationType } from '@/types/user';

import { findActiveDoc } from '@/utils/document';
import { blobUrlToBase64 } from '@/utils/file';

interface UseIdmissionVerificationOptions {
  type?: 'id' | 'face';
  isSelfieChecked?: boolean;
  isLivenessVideoRecorded?: boolean;
  autoInit?: boolean;
  minorClientId?: number;
  initialAcknowledgeRejection?: boolean;
  onCompleted?: () => void;
}

// See:https://documentation.idmission.com/identity/#appendix-e-id-types-codes
const idDocumentTypeMap: Partial<Record<DocumentType, string>> = {
  [DocumentType.Id]: 'NID',
  [DocumentType.Passport]: 'PP',
  [DocumentType.DriverLicense]: 'DL',
  [DocumentType.VoterID]: 'VID',
};

export const useIdmissionVerification = (
  options: UseIdmissionVerificationOptions = {},
) => {
  const {
    type = 'id',
    isSelfieChecked = false,
    isLivenessVideoRecorded = false,
    autoInit = true,
    minorClientId,
    initialAcknowledgeRejection = false,
    onCompleted,
  } = options;

  const isRecordDisabled = useAppSelector(selectLivenessrecordDisabled);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [verificationResult, setVerificationResult] =
    useState<IdentityVerificationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acknowledgeRejection, setAcknowledgeRejection] = useState(
    initialAcknowledgeRejection,
  );
  const [idImageBase64, setIdImageBase64] = useState<string | null>(null);
  const [idImageLoading, setIdImageLoading] = useState(false);
  const user = useAppSelector(selectLoggedInUser);
  const operationUser = useAppSelector(selectOperationUser);
  const operationData = useAppSelector(selectOperationData);
  const { idCaptureRequirement, idDocumentTypes: acceptedIdTypes } =
    useAppSelector(selectConfigsState);
  const retryCount = useRef(0);
  const location = useLocation();

  const remediationOperationId =
    (user?.type === OperationType.Remediation && user?.operationId) ||
    undefined;

  const { startRecording, stopRecording } = useLivenessCheckRecorder({
    minorClientId,
    operationId: remediationOperationId,
  });
  const { t } = useTranslate();
  const dispatch = useAppDispatch();

  const isMinorMode = minorClientId !== undefined;

  const shouldRecordLiveness = useMemo(
    () => !isMinorMode && !isRecordDisabled && !isLivenessVideoRecorded,
    [isMinorMode, isRecordDisabled, isLivenessVideoRecorded],
  );
  const shouldValidateFace =
    !isMinorMode && type === 'id' && (!isSelfieChecked || shouldRecordLiveness);

  const isIDRejected = useMemo(() => {
    const docsSource = isMinorMode ? operationUser?.documents : user?.documents;
    const idDocs = docsSource?.filter(
      item => item.type === DocumentSection.Identity,
    );

    return (
      idDocs !== undefined &&
      idDocs.length > 0 &&
      idDocs.every(item => item.status === DocumentStatus.Rejected)
    );
  }, [isMinorMode, operationUser, user]);
  const { selfieStatus: userSelfieStatus, livenessStatus: userLivenessStatus } =
    useAppSelector(selectLivenessStatuses);
  const {
    selfieStatus: operationSelfieStatus,
    livenessStatus: operationLivenessStatus,
  } = useAppSelector(selectOperationLivenessStatuses);

  // For minor eKYC (Right Start), use the minor's statuses from operation state;
  // for the adult's own flow, use user-data state.
  const selfieStatus = isMinorMode ? operationSelfieStatus : userSelfieStatus;
  const livenessStatus = isMinorMode
    ? operationLivenessStatus
    : userLivenessStatus;
  const isSelfieRejected = selfieStatus === DocumentStatus.Rejected;
  const isLivenessRejected = livenessStatus === DocumentStatus.Rejected;

  useEffect(() => {
    if (autoInit && !isProcessing && !sessionId && !verificationResult) {
      startVerification().catch(err => {
        setError(t('verification.idmission.errors.initFailed'));
        console.error('Failed to initialize verification:', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoInit, isProcessing, sessionId, verificationResult, t]);

  useEffect(() => {
    const fetchIdImage = async () => {
      try {
        setIdImageLoading(true);

        // In minor mode, use the minor's uploaded ID documents for face match;
        // for the adult's own flow, use the logged-in user's documents.
        const documents = isMinorMode
          ? operationUser?.documents || []
          : user?.documents || [];

        const base64Data = await fetchIdentityImageAsBase64(documents);

        if (base64Data) {
          setIdImageBase64(base64Data);
        }
      } catch (error) {
        console.error('Failed to fetch identity image:', error);
        setError(t('verification.idmission.errors.fetchIdentityImageFailed'));
      } finally {
        setIdImageLoading(false);
      }
    };

    if (type === 'face' && !isSelfieChecked) {
      fetchIdImage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, isSelfieChecked, isMinorMode, operationUser, user]);

  const handleAcknowledgeRejection = () => {
    setAcknowledgeRejection(true);
  };

  const resetVerification = () => {
    setError(null);
    setIsProcessing(false);
    setIsCompleted(false);
    setVerificationResult(null);
    setSessionId(null);
  };

  const isValidIdType = useCallback(
    (idType: string): boolean => {
      return acceptedIdTypes.some(type => idDocumentTypeMap[type] === idType);
    },
    [acceptedIdTypes],
  );

  const startVerification = async () => {
    try {
      setVerificationResult(null);
      setIsProcessing(true);

      const res = await initIdmissionSession();
      setSessionId(res.session.id);
      return res;
    } catch (error) {
      console.error('Failed to create verification session:', error);
      setError(t('verification.idmission.errors.createSessionFailed'));
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const processIdmissionResult = async (
    res: IdentityVerificationResponse,
  ): Promise<boolean> => {
    try {
      setVerificationResult(res);

      await submitIdmissionResult(
        {
          resultId: res.resultData.verificationResultId,
          ...(minorClientId !== undefined && { clientId: minorClientId }),
          // The backend will update the documents based on the IDM results.
          shouldUpdateIdentity: true,
          shouldUpdateSelfie: true,
          shouldUpdateIdVideo: true,
        },
        { operationId: remediationOperationId },
      );

      const operationId = user?.operationId;
      const sectionToCheck =
        type === 'face' ? DocumentSection.Selfie : DocumentSection.Identity;
      const result = await waitForProcessDataCompleted(
        isMinorMode && operationId
          ? { operationId, minorClientId: minorClientId! }
          : undefined,
        sectionToCheck,
      );

      if (result.isCompleted) {
        if (isMinorMode && result.minorData) {
          dispatch(
            setOperationData({
              user: result.minorData.user,
              requiredSections: result.minorData.requiredSections,
              accountTypes: operationData.accountTypes,
            }),
          );
        } else {
          dispatch(notifyUserChanged());
        }
        return true;
      } else {
        console.error('Failed to process idmission result');
        setError(t('verification.idmission.errors.processingFailed'));
        return false;
      }
    } catch (error) {
      console.error('Failed to process idmission result:', error);
      setError(t('verification.idmission.errors.processingFailed'));
      return false;
    }
  };

  const handleBeforeLivenessCheck = (req: LivenessCheckRequest) => {
    if (shouldRecordLiveness) {
      startRecording();
    }
    return Promise.resolve(req);
  };

  const handleError = (error: Error) => {
    const MAX_ATTEMPTS = 2;
    const attempt = retryCount.current + 1;

    console.warn(`IDMission validation failed (${attempt}):`, error.message);

    if (attempt < MAX_ATTEMPTS) {
      retryCount.current++;

      if (IS_PRODUCTION) {
        throw error;
      }

      // In dev/staging, allow to bypass errors for quick testing
      const bypassIdmissionErrors =
        localStorage.getItem('bypassIdmissionErrors') === 'true';

      if (!bypassIdmissionErrors) {
        throw error;
      }
    }
  };

  const handleComplete = useCallback(
    async (response: SubmissionResponse | null): Promise<void> => {
      try {
        setIsProcessing(true);
        if (!user?.tenant) {
          throw new Error('Tenant not set');
        }

        if (!response) {
          throw new Error(
            t('verification.idmission.errors.extractedDataUnavailable'),
          );
        }

        // There is a type mismatch between the SDK and the actual response
        // so we need to cast the response to the correct type
        const resp = response as unknown as IdentityVerificationResponse;

        for (const decision in resp.resultData?.verificationResultDetails) {
          const decisionResult =
            resp.resultData.verificationResultDetails[
              decision as keyof VerificationResultDetails
            ];
          if (
            I18N_IDMISSION_FAILED_CODE_MESSAGE_MAP[
              decisionResult.DecisionStateCode
            ]
          ) {
            handleError(
              new Error(
                t(
                  I18N_IDMISSION_FAILED_CODE_MESSAGE_MAP[
                    decisionResult.DecisionStateCode
                  ],
                ),
              ),
            );
          }
        }

        const extractedIdData = resp.responseCustomerData?.extractedIdData;
        if (extractedIdData && !isValidIdType(extractedIdData.idType)) {
          handleError(
            new Error(t('verification.idmission.errors.invalidIdType')),
          );
        }

        if (shouldRecordLiveness) {
          try {
            await stopRecording({ minimumDuration: 10000 });
          } catch (error) {
            console.error('Failed to stop recording:', error);
          }
        }

        const isProcessed = await processIdmissionResult(resp);
        if (!isProcessed) {
          return;
        }

        setIsCompleted(true);
        retryCount.current = 0;
        if (location.state?.notNavigateByStatus) {
          delete location.state?.notNavigateByStatus;
        }
        onCompleted?.();
      } catch (e) {
        if (e instanceof Error) {
          const msg = e.message.trim().toLowerCase();
          setError(
            msg.includes('access denied')
              ? t('verification.idmission.errors.cameraOrPermissionDenied')
              : e.message,
          );
          Bugfender.error('IDMission verification error:', e.message);
        } else {
          console.error('Unknown error:', e);
          Bugfender.error('IDMission verification unknown error:', e);
        }
      } finally {
        setIsProcessing(false);
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isValidIdType,
      location.state?.notNavigateByStatus,
      shouldRecordLiveness,
      stopRecording,
    ],
  );

  const handleOnRequestFailure = (error: Error) => {
    console.error('IDMission request failure:', error);
    Bugfender.error('IDMission request failure:', error.message);
  };

  return {
    sessionId,

    idImageBase64,
    idImageLoading,
    isIDRejected,
    isSelfieRejected,
    isLivenessRejected,
    acknowledgeRejection,
    isProcessing,
    isCompleted,
    verificationResult,
    error,
    shouldValidateFace,
    idCaptureRequirement,
    startVerification,
    processIdmissionResult,
    handleBeforeLivenessCheck,
    handleComplete,
    handleAcknowledgeRejection,
    resetVerification,
    handleOnRequestFailure,
  };
};

type WaitForMinorOptions = {
  operationId: string;
  minorClientId: number;
};

type WaitForProcessDataResult =
  | { isCompleted: false; minorData: undefined }
  | {
      isCompleted: true;
      minorData:
        | Awaited<ReturnType<typeof getHolderDetailAndRequiredDocumentsRequest>>
        | undefined;
    };

async function waitForProcessDataCompleted(
  minorOptions?: WaitForMinorOptions,
  documentSection: DocumentSection = DocumentSection.Identity,
): Promise<WaitForProcessDataResult> {
  const MAX_ATTEMPTS = 60;
  const INTERVAL_MS = 1000;

  const activeStatuses = [
    DocumentStatus.InReview,
    DocumentStatus.Expired,
    DocumentStatus.AboutToExpire,
  ];

  const hasProcessedDoc = (documents?: Document[]) =>
    documents?.some(
      d => d.type === documentSection && activeStatuses.includes(d.status),
    );

  for (let _attempt = 0; _attempt < MAX_ATTEMPTS; _attempt++) {
    try {
      if (minorOptions) {
        const minorData = await getHolderDetailAndRequiredDocumentsRequest(
          minorOptions.operationId,
          minorOptions.minorClientId,
        );
        if (hasProcessedDoc(minorData?.user?.documents)) {
          return { isCompleted: true, minorData };
        }
      } else {
        const data = await getCurrentUser();
        if (hasProcessedDoc(data?.user?.documents)) {
          return { isCompleted: true, minorData: undefined };
        }
      }
    } catch (error) {
      console.error(
        'Failed to check user info updated after processing IDMission result:',
        error,
      );
    }
    await new Promise(res => setTimeout(res, INTERVAL_MS));
  }
  return { isCompleted: false, minorData: undefined };
}

export async function fetchIdentityImageAsBase64(
  documents: Document[],
): Promise<string | null> {
  const activeIdentityDoc = findActiveDoc(documents, DocumentSection.Identity);

  if (!activeIdentityDoc?.uri) {
    console.warn('No active identity document found');
    return null;
  }

  let blobUrl: string | null = null;
  try {
    blobUrl = await getLocalDocumentUrlFromS3(activeIdentityDoc.uri);
    const base64Data = await blobUrlToBase64(blobUrl);
    return base64Data;
  } finally {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
    }
  }
}
